/**
 * PostureSnapshotTimeline — Iter 87.
 *
 * Read-only horizontal scrollable strip of every posture snapshot a patient
 * has saved to chart, sorted oldest → newest. Each tile shows the view label
 * (anterior / lateral_l / etc), captured date, and clinical notes.
 *
 * Designed to slot into the PatientChart page below the SOAP / progress
 * sections — gives the clinician a visual "before / after" timeline at a
 * glance without leaving the chart.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Camera, FileDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api, BACKEND_URL } from '../lib/api';

export default function PostureSnapshotTimeline({ patientId }) {
  const [snaps, setSnaps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!patientId) { setLoading(false); return; }
    let stop = false;
    api.get(`/posture/${patientId}/snapshots`)
      .then(({ data }) => { if (!stop) setSnaps((data || []).slice().sort(
        (a, b) => (a.captured_at || '').localeCompare(b.captured_at || '')
      )); })
      .catch(() => { /* swallow — empty timeline is fine */ })
      .finally(() => { if (!stop) setLoading(false); });
    return () => { stop = true; };
  }, [patientId]);

  if (loading) return (
    <div className="bento-card flex items-center justify-center py-6"
         data-testid="posture-timeline-loading">
      <Loader2 className="w-5 h-5 animate-spin text-fg-muted"/>
    </div>
  );
  if (snaps.length === 0) return (
    <div className="bento-card text-sm text-fg-muted flex items-center gap-3"
         data-testid="posture-timeline-empty">
      <Camera className="w-4 h-4"/>
      No posture snapshots yet.
      <Link to={`/admin/posture-capture?patientId=${patientId}`}
            className="text-brand hover:underline ml-auto"
            data-testid="posture-timeline-capture-link">
        Capture one →
      </Link>
    </div>
  );

  // Group by capture day so we can show monthly anchors above each tile group
  const grouped = snaps.reduce((acc, s) => {
    const d = (s.captured_at || '').slice(0, 10) || 'unknown';
    (acc[d] = acc[d] || []).push(s);
    return acc;
  }, {});
  const dayKeys = Object.keys(grouped).sort();

  return (
    <div className="bento-card" data-testid="posture-timeline">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <h3 className="font-heading font-semibold flex items-center gap-2">
          <Camera className="w-4 h-4 text-brand"/>
          Posture timeline ({snaps.length})
        </h3>
        <div className="flex gap-2">
          <Link to={`/admin/posture-capture?patientId=${patientId}`}
                className="inline-flex items-center gap-1 text-xs text-brand hover:underline"
                data-testid="posture-timeline-add">
            <Camera className="w-3 h-3"/> Capture more
          </Link>
        </div>
      </div>

      <div className="overflow-x-auto pb-2" data-testid="posture-timeline-scroll">
        <div className="flex gap-4 min-w-max">
          {dayKeys.map((day, dayIdx) => (
            <React.Fragment key={day}>
              {/* Day anchor */}
              <div className="flex flex-col gap-2">
                <div className="text-[10px] uppercase tracking-wider text-fg-muted text-center font-semibold sticky top-0">
                  {new Date(day).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' })}
                </div>
                <div className="flex gap-2">
                  {grouped[day].map((s) => (
                    <div key={s.snapshot_id}
                         className="border border-soft rounded-md overflow-hidden bg-bg-secondary/40 w-32 flex-shrink-0"
                         data-testid={`posture-timeline-tile-${s.snapshot_id}`}>
                      <img alt={`${s.view_label} on ${day}`}
                           src={`${BACKEND_URL}/api/posture/${patientId}/snapshots/${s.snapshot_id}`}
                           className="w-full h-20 object-cover bg-black"
                           loading="lazy"/>
                      <div className="p-1.5 text-[10px] leading-tight">
                        <div className="font-semibold truncate">{s.view_label}</div>
                        {s.note && <div className="text-fg-muted line-clamp-2 mt-0.5" title={s.note}>{s.note}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Visual separator between days (skip after last) */}
              {dayIdx < dayKeys.length - 1 && (
                <div className="flex flex-col items-center justify-center pt-4">
                  <div className="w-8 h-px bg-soft" aria-hidden="true"/>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="mt-3 text-[11px] text-fg-muted flex items-center gap-1.5">
        <FileDown className="w-3 h-3"/>
        Latest 4 snapshots auto-staple onto the 30-day GP outcomes report.
      </div>
    </div>
  );
}
